import React, { DragEvent, useCallback } from "react";
import { useFileUpload } from "../useFileUpload";
import { FileUploadConfig, UploadUserInfo } from "../types";
import { DEFAULT_CONFIG } from "../utils";
import { ATTACHMENT_FIELD_STYLES } from "./styles";
import DragDropZone from "./DragDropZone";
import SelectedFileList from "./SelectedFileList";
import FileUploadInfoDisplay from "./FileUploadInfoDisplay";
import ValidationErrorDisplay from "./ValidationErrorDisplay";
import { IAttachment } from "../../approval/approval-form/approval-form";

interface AttachmentFieldProps {
  config?: Partial<FileUploadConfig>;
  onFilesChange?: (files: FileList | null) => void;
  setUserInfo: (userInfo: UploadUserInfo) => void;
  uuid: string;
  attachments?: IAttachment[];
  disabled?: boolean;
}

const AttachmentField: React.FC<AttachmentFieldProps> = ({
  config,
  onFilesChange,
  setUserInfo,
  uuid,
  attachments,
  disabled = false
}) => {
  const mergedConfig = { ...DEFAULT_CONFIG, ...config };

  const {
    selectedFiles,
    isDragOver,
    validationErrors,
    addFiles,
    removeFile,
    setDragOver,
    config: uploadConfig
  } = useFileUpload(mergedConfig, setUserInfo, uuid);

  React.useEffect(() => {
    onFilesChange?.(selectedFiles);
  }, [selectedFiles, onFilesChange]);

  const handleDragOver = useCallback(
    (event: DragEvent<HTMLDivElement>): void => {
      if (disabled) return;
      event.preventDefault();
      setDragOver(true);
    },
    [disabled, setDragOver]
  );

  const handleDragLeave = useCallback(
    (event: DragEvent<HTMLDivElement>): void => {
      if (disabled) return;
      event.preventDefault();
      setDragOver(false);
    },
    [disabled, setDragOver]
  );

  const handleDrop = useCallback(
    (event: DragEvent<HTMLDivElement>): void => {
      if (disabled) return;
      event.preventDefault();
      setDragOver(false);

      const files = event.dataTransfer.files;
      if (files && files.length > 0) {
        addFiles(files);
      }
    },
    [disabled, setDragOver, addFiles]
  );

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      if (disabled) return;
      const files = event.target.files;
      if (files && files.length > 0) {
        addFiles(files);
      }
    },
    [disabled, addFiles]
  );

  const handleRemoveFile = useCallback(
    (index: number): void => {
      removeFile(index);
    },
    [removeFile]
  );

  return (
    <div style={ATTACHMENT_FIELD_STYLES.container}>
      {!disabled && (
        <DragDropZone
          isDragOver={isDragOver}
          disabled={disabled}
          acceptedFileTypes={uploadConfig.acceptedFileTypes}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onFileSelect={handleFileSelect}
        />
      )}

      {!disabled && <FileUploadInfoDisplay config={uploadConfig} />}

      {!disabled && <ValidationErrorDisplay errors={validationErrors} />}

      <SelectedFileList
        files={selectedFiles}
        disabled={disabled}
        onRemoveFile={handleRemoveFile}
        attachments={attachments ?? []}
      />
    </div>
  );
};

export default AttachmentField;
